import React, { FC } from 'react'

import { getPlansSlugs, planCollectionNames } from '@smarty-nx/contentful'
import { Plan } from '@smarty-nx/data'
import { Typography } from '@smarty-nx/ds'

import { content } from './plan-section-phase2.content'
import classes from './plan-section-phase2.module.scss'
import CheckImage from '../../static/images/checkmark.svg'
import { matchPlanMeta } from '../../utils/plans'
import { PlanData, PlanMeta } from '../../utils/plans.types'
import { TooltipModal } from '../Modals'
import { PlanCardSmall } from '../PlanCardSmall/PlanCardSmall'
import { CheckIcon } from '../shared'

const VOICE_PLAN_SLUGS = getPlansSlugs(planCollectionNames.VOICE_PLANS)
const DATA_ONLY_PLAN_SLUGS = getPlansSlugs(planCollectionNames.DATA_ONLY_PLANS)

const { voicePlans, dataOnlyPlans } = content

interface PlanSectionProps {
  currentPlan?: PlanMeta
  plans?: Plan[]
}

const renderVoicePlansHeader = () => (
  <div className={classes.headingWithCoverage}>
    <div className={classes.headingsWrapper}>
      <div className={classes.mainHeadingWrapper}>
        <Typography
          component="h2"
          variant="Kollektif/Large"
          variantSm="Kollektif/ExtraLarge"
          className={classes.headingSecondary}
          dataTestId="voice-plans-heading"
        >
          {voicePlans.heading}
        </Typography>
        <TooltipModal header={voicePlans.tooltipHeader}>
          <Typography
            variant="Kollektif/ExtraSmall"
            variantSm="Kollektif/Small"
            className={classes.tooltipHeading}
          >
            {voicePlans.tooltipLines[0]}
          </Typography>
          <Typography
            variant="Kollektif/ExtraSmall"
            variantSm="Kollektif/Small"
            className={classes.tooltipHeading}
          >
            {voicePlans.tooltipLines[1]}
          </Typography>
          <Typography
            variant="Kollektif/ExtraSmall"
            variantSm="Kollektif/Small"
            className={classes.tooltipHeading}
          >
            <strong>{voicePlans.tooltipEsimLine}</strong>
          </Typography>
        </TooltipModal>
      </div>
      <h2
        className={classes.headingSecondarySmall}
        id="voice-plans-small-heading"
        data-testid="voice-plans-small-heading"
      >
        {voicePlans.smallHeading}
      </h2>
    </div>
  </div>
)

const renderDataOnlyPlansHeader = () => (
  <div className={classes.headingWithCoverage}>
    <div className={classes.headingsWrapper}>
      <div className={classes.mainHeadingWrapper}>
        <Typography
          component="h2"
          variant="Kollektif/Large"
          variantSm="Kollektif/ExtraLarge"
          className={classes.headingSecondary}
          dataTestId="data-only-plans-heading"
        >
          {dataOnlyPlans.heading}
        </Typography>
        <TooltipModal header={dataOnlyPlans.tooltipHeader}>
          <Typography
            variant="Kollektif/ExtraSmall"
            variantSm="Kollektif/Small"
            className={classes.tooltipHeading}
          >
            {dataOnlyPlans.tooltipIntro}
          </Typography>
          <ul className={classes.list}>
            {dataOnlyPlans.tooltipBullets.map((bullet) => (
              <li key={bullet} className={classes.listItem}>
                <CheckIcon src={CheckImage} alt="" />
                <Typography
                  variant="Kollektif/ExtraSmall"
                  className={classes.tooltipPara}
                >
                  {bullet}
                </Typography>
              </li>
            ))}
          </ul>
          <Typography
            variant="Kollektif/ExtraSmall"
            variantSm="Kollektif/Small"
            className={classes.tooltipHeading}
          >
            <strong>{dataOnlyPlans.tooltipEsimLine}</strong>
          </Typography>
          <Typography
            variant="Kollektif/ExtraSmall"
            variantSm="Kollektif/Small"
            className={classes.tooltipHeading}
          >
            <strong>Note: </strong>
            {dataOnlyPlans.tooltipNote}
          </Typography>
        </TooltipModal>
      </div>
      <h2
        className={classes.headingSecondarySmall}
        id="data-only-plans-small-heading"
        data-testid="data-only-plans-small-heading"
      >
        {dataOnlyPlans.smallHeading}
      </h2>
    </div>
  </div>
)

export const PlanSection: FC<PlanSectionProps> = ({ currentPlan, plans }) => (
  <div className={classes.plansSectionContainer} id="plans">
    <Typography
      component="h2"
      variant="Kollektif/Large"
      variantSm="Kollektif/ExtraLarge"
      className={classes.plansSectionHeading}
    >
      {content.mainHeading}
    </Typography>
    {renderVoicePlansHeader()}
    <div className={classes.plansWrapper} data-testid="voice-plans">
      {VOICE_PLAN_SLUGS.map((p, index) => {
        const result = matchPlanMeta(plans ?? [], p)
        if (!result) {
          return null
        }
        const isCurrentPlan = currentPlan?.slug && p === currentPlan.slug
        const { priceData, planMeta } = result
        return (
          <PlanCardSmall
            key={p}
            index={index}
            to={isCurrentPlan ? undefined : `/plans/${p}`}
            href={isCurrentPlan ? `/order/${p}` : undefined}
            planMeta={planMeta}
            priceData={priceData as unknown as PlanData}
            position={index + 1}
            cta={isCurrentPlan ? 'Get this' : 'View'}
            parentElModule="PlanSectionPhase2"
          />
        )
      })}
    </div>
    {renderDataOnlyPlansHeader()}
    <div className={classes.plansWrapper} data-testid="data-only-plans">
      {DATA_ONLY_PLAN_SLUGS.map((p, index) => {
        const result = matchPlanMeta(plans ?? [], p)
        if (!result) {
          return null
        }
        const isCurrentPlan = currentPlan?.slug && p === currentPlan.slug
        const { priceData, planMeta } = result
        return (
          <PlanCardSmall
            key={p}
            index={index}
            to={isCurrentPlan ? undefined : `/plans/${p}`}
            href={isCurrentPlan ? `/order/${p}` : undefined}
            planMeta={planMeta}
            priceData={priceData as unknown as PlanData}
            position={index + VOICE_PLAN_SLUGS.length + 1}
            cta={isCurrentPlan ? 'Get' : 'View'}
            isPlanDataOnly
            parentElModule="PlanSectionPhase2"
          />
        )
      })}
    </div>
  </div>
)

export default PlanSection
